import { Currency, toCurrency } from "@redotech/money/currencies";
import { useLoad } from "@redotech/react-util/load";
import {
  CURRENCY_FORMAT,
  CurrencyContext,
  DEFAULT_CURRENCY,
} from "@redotech/redo-web/currency";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { createContext, memo, useContext, useState } from "react";
import { Outlet, useParams } from "react-router-dom";
import { getCurrencyExchangeRates, getOrderById } from "../api";
import { SettingsContext } from "../app/settings";
import { StorefrontCartContextProvider } from "../contexts/StorefrontCartContext";
import { getReturnableItems, makeOrder } from "../utils/utils";
import { Page } from "./page";

// FIXME bad any type
export const OrderContext = createContext<any>(undefined);

export const Order = memo(() => {
  const [currency, setCurrency] = useState<Currency>(DEFAULT_CURRENCY);

  const { id } = useParams();

  const settings = useContext(SettingsContext);

  const orderLoad = useLoad(async () => {
    const res = await getOrderById(
      id,
      settings.orderTracking?.enabled ?? false,
    );
    const order = await makeOrder(res.data?.order, settings);
    const returnableItems = await getReturnableItems(
      order,
      settings,
      settings.bundleRulesList,
    );
    order.returnableItems = returnableItems;

    let usdRatesPromise;

    const shopCurrency = order?.shopify?.currency ?? DEFAULT_CURRENCY;
    const orderPresentmentCurrency =
      toCurrency(order?.shopify?.presentment_currency) ?? DEFAULT_CURRENCY;

    if (orderPresentmentCurrency !== "USD") {
      usdRatesPromise = getCurrencyExchangeRates("USD");
    }

    let formatCurrency: (amount: number) => string;
    if (orderPresentmentCurrency !== shopCurrency) {
      try {
        const exchangeRates = (await getCurrencyExchangeRates(shopCurrency))
          .data;
        formatCurrency = exchangeRates
          ? (amount) => {
              return CURRENCY_FORMAT(orderPresentmentCurrency).format(
                amount * exchangeRates[orderPresentmentCurrency],
              );
            }
          : undefined;
      } catch (e) {
        console.error(e);
      }
    }
    if (formatCurrency === undefined) {
      formatCurrency = (amount) => CURRENCY_FORMAT(shopCurrency).format(amount);
    }

    const usdRates = usdRatesPromise ? (await usdRatesPromise).data : undefined;
    const formatCurrencyFromUSD = usdRates
      ? (amount) => {
          return CURRENCY_FORMAT(orderPresentmentCurrency).format(
            amount * usdRates[orderPresentmentCurrency],
          );
        }
      : (amount) => CURRENCY_FORMAT("USD").format(amount);

    return {
      order,
      orderCurrency: orderPresentmentCurrency,
      formatCurrency,
      formatCurrencyFromUSD,
    };
  }, []);

  orderLoad.error && console.error(orderLoad.error);

  if (!orderLoad.pending && orderLoad.error) {
    return (
      <Page>
        <div>There was an error loading this order</div>
      </Page>
    );
  }

  if (orderLoad.pending || !orderLoad.value) {
    return (
      <Page>
        <LoadingRedoAnimation />
      </Page>
    );
  }

  const { order, orderCurrency, formatCurrency, formatCurrencyFromUSD } =
    orderLoad.value;
  if (currency !== orderCurrency) {
    setCurrency(orderCurrency);
  }

  return (
    <OrderContext.Provider value={order}>
      <CurrencyContext.Provider
        value={{
          currency,
          formatCurrency,
          setCurrency,
          formatCurrencyFromUSD,
        }}
      >
        <StorefrontCartContextProvider>
          <Page>
            <Outlet />
          </Page>
        </StorefrontCartContextProvider>
      </CurrencyContext.Provider>
    </OrderContext.Provider>
  );
});
